import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Settings, Check, AlertCircle, Lock, Package } from 'lucide-react';

const OrganizationComponentSettingsModal = ({ organization, onClose, onUpdate }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Check if components are locked by a package
  const isLockedByPackage = settings?.components_locked_by_package || false;
  const activePackageName = settings?.active_package_name || 'Unknown Package';

  const componentLabels = {
    'commercial_flight': 'Commercial Flight',
    'private_flight': 'Private Flight',
    'hotel': 'Hotel',
    'restaurant': 'Restaurant',
    'meeting': 'Meeting',
    'event': 'Event',
    'rental_car': 'Rental Car',
    'chauffeur_service': 'Chauffeur Service',
    'memo': 'Memo'
  };

  const componentDescriptions = {
    'commercial_flight': 'Book or add commercial airline flights',
    'private_flight': 'Charter flights and private aviation',
    'hotel': 'Hotel accommodations and lodging',
    'restaurant': 'Dining reservations and meals',
    'meeting': 'Business meetings and appointments',
    'event': 'Conferences, seminars, and events',
    'rental_car': 'Vehicle rentals and transportation',
    'chauffeur_service': 'Private driver and car service',
    'memo': 'Notes and general information'
  };

  useEffect(() => {
    loadSettings();
  }, [organization.id]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await axios.get(`/organizations/${organization.id}/component-settings`);
      setSettings(response.data);
    } catch (err) {
      console.error('Error loading component settings:', err);
      setError('Failed to load component settings');
    } finally {
      setLoading(false);
    }
  };

  const handleComponentToggle = (componentType) => {
    if (!settings || isLockedByPackage) return;

    const currentActive = settings.active_components || [];
    const isActive = currentActive.includes(componentType);

    let newActiveComponents;
    if (isActive) {
      // Remove component
      newActiveComponents = currentActive.filter(c => c !== componentType);
    } else {
      // Add component
      newActiveComponents = [...currentActive, componentType];
    }

    setSettings({
      ...settings,
      active_components: newActiveComponents
    });
  };

  const handleSave = async () => {
    if (!settings) return;

    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');

      const response = await axios.put(`/organizations/${organization.id}/component-settings`, {
        active_components: settings.active_components
      });

      setSettings(response.data);
      setSuccessMessage('Component settings updated successfully');
      
      // Call onUpdate callback if provided
      if (onUpdate) {
        onUpdate(response.data);
      }

      // Auto-close success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);

    } catch (err) {
      console.error('Error saving component settings:', err);
      setError(err.response?.data?.detail || 'Failed to save component settings');
    } finally {
      setSaving(false);
    }
  };

  const allComponents = Object.keys(componentLabels);
  const activeCount = settings?.active_components?.length || 0;

  return (
    <div className="fixed inset-0 bg-ui-overlay bg-opacity-50 flex items-center justify-center z-[70]">
      <div className="bg-ui-surface rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-ui-line">
          <div className="flex items-center space-x-3">
            <Settings className="h-6 w-6 text-ui-link" />
            <div>
              <h2 className="text-xl font-semibold text-ui-ink">Component Settings</h2>
              <p className="text-sm text-ui-muted">{organization.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-ui-muted hover:text-ui-ink-3 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-160px)]">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ui-primary"></div>
              <span className="ml-3 text-ui-ink-3">Loading settings...</span>
            </div>
          ) : error ? (
            <div className="bg-ui-red-bg border border-ui-red-line rounded-md p-4 mb-6">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-ui-red-fg mr-2" />
                <span className="text-ui-red-fg">{error}</span>
              </div>
            </div>
          ) : (
            <>
              {/* Success Message */}
              {successMessage && (
                <div className="bg-ui-green-bg border border-ui-green-line rounded-md p-4 mb-6">
                  <div className="flex items-center">
                    <Check className="h-5 w-5 text-ui-green-fg mr-2" />
                    <span className="text-ui-green-fg">{successMessage}</span>
                  </div>
                </div>
              )}

              {/* Package Lock Notice */}
              {isLockedByPackage && (
                <div className="mb-6 bg-ui-amber-bg border border-ui-amber-line rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center w-10 h-10 bg-ui-amber-bg rounded-full">
                        <Lock className="h-5 w-5 text-ui-amber-fg" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-ui-amber-fg mb-1">
                        Components Managed by Package
                      </h4>
                      <p className="text-sm text-ui-amber-fg mb-2">
                        This organization's available components are controlled by the assigned package:
                      </p>
                      <div className="flex items-center space-x-2 bg-ui-amber-bg rounded-md px-3 py-2 inline-flex">
                        <Package className="h-4 w-4 text-ui-amber-fg" />
                        <span className="font-medium text-ui-amber-fg">{activePackageName}</span>
                      </div>
                      <p className="text-xs text-ui-amber-fg mt-2">
                        To modify components manually, remove the package assignment from this organization.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Settings Overview */}
              <div className="mb-6">
                <h3 className="text-lg font-medium text-ui-ink mb-2">Component Access Control</h3>
                <p className="text-ui-ink-3 mb-4">
                  {isLockedByPackage
                    ? 'These components are currently managed by the assigned package and cannot be modified here.'
                    : 'Control which component types are available when users add components to trips. Inactive components will not appear in the "Add Component" list.'
                  }
                </p>

                <div className="bg-ui-blue-bg border border-ui-blue-line rounded-md p-3">
                  <div className="text-sm text-ui-blue-fg">
                    <strong>{activeCount} of {allComponents.length}</strong> component types are currently active
                    {isLockedByPackage && <span className="ml-2 text-ui-amber-fg">(read-only)</span>}
                  </div>
                </div>
              </div>

              {/* Component List */}
              <div className="space-y-3">
                <h4 className="text-md font-medium text-ui-ink mb-3">Available Component Types</h4>
                
                {allComponents.map((componentType) => {
                  const isActive = settings?.active_components?.includes(componentType) ?? true;

                  return (
                    <div
                      key={componentType}
                      className={`flex items-center justify-between p-4 border rounded-lg transition-colors ${
                        isActive
                          ? 'border-ui-green-line bg-ui-green-bg'
                          : 'border-ui-line bg-ui-sunk'
                      } ${isLockedByPackage ? 'opacity-75' : ''}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <div className={`w-3 h-3 rounded-full ${
                            isActive ? 'bg-ui-green-strong' : 'bg-ui-faint'
                          }`} />
                          <div>
                            <h5 className="font-medium text-ui-ink">
                              {componentLabels[componentType]}
                            </h5>
                            <p className="text-sm text-ui-ink-3">
                              {componentDescriptions[componentType]}
                            </p>
                          </div>
                        </div>
                      </div>

                      {isLockedByPackage ? (
                        <div className={`px-4 py-2 rounded-md text-sm font-medium flex items-center space-x-1 ${
                          isActive
                            ? 'bg-ui-green-bg text-ui-green-fg'
                            : 'bg-ui-hover text-ui-muted'
                        }`}>
                          <Lock className="h-3 w-3" />
                          <span>{isActive ? 'Active' : 'Inactive'}</span>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleComponentToggle(componentType)}
                          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            isActive
                              ? 'bg-ui-green-strong hover:bg-ui-green-strong/90 text-ui-on-primary'
                              : 'bg-ui-ink hover:bg-ui-ink text-ui-surface'
                          }`}
                        >
                          {isActive ? 'Active' : 'Inactive'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Warning for no active components */}
              {activeCount === 0 && (
                <div className="mt-4 bg-ui-amber-bg border border-ui-amber-line rounded-md p-4">
                  <div className="flex items-center">
                    <AlertCircle className="h-5 w-5 text-ui-amber-fg mr-2" />
                    <span className="text-ui-amber-fg text-sm">
                      Warning: No component types are active. Users will not be able to add any components to trips.
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-ui-line bg-ui-sunk">
          <button
            onClick={onClose}
            className="px-4 py-2 text-ui-ink-2 bg-ui-surface border border-ui-control rounded-md hover:bg-ui-hover transition-colors"
          >
            {isLockedByPackage ? 'Close' : 'Cancel'}
          </button>
          {!isLockedByPackage && (
            <button
              onClick={handleSave}
              disabled={saving || loading}
              className="px-4 py-2 bg-ui-primary hover:bg-ui-primary-hover text-ui-on-primary rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-ui-surface inline-block mr-2"></div>
                  Saving...
                </>
              ) : (
                'Save Settings'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrganizationComponentSettingsModal;